import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import houseImage from '../assets/house.jpg';
import stewartLogo from '../assets/stewart_logo.png';
import { NotificationType, useNotification } from "../context/NotificationContext.tsx";
import { useAuthApi } from '../context/ApiContext';
import { ResetTokenStatus } from '../api/ApiTypes.ts';
import { isPasswordValid as meetsRules, PasswordRequirements, PasswordValidation, validatePassword } from '../components/PasswordRequirements.tsx';

// what the appraiser reads for a link that can no longer be used; matches PasswordResetService
const LINK_MESSAGES: Record<Exclude<ResetTokenStatus['status'], 'VALID'>, string> = {
    EXPIRED: 'This reset link has expired. Request a new one.',
    USED: 'This reset link has already been used. Request a new one if you still need to reset your password.',
    UNKNOWN: 'This reset link is no longer valid. Request a new one.',
};
const CHECK_FAILED = "We couldn't check this link right now. Try again in a few minutes, or request a new one.";

export const ResetPasswordLanding = () => {
    const [token, setToken] = useState('');
    const [username, setUsername] = useState('');
    const [linkProblem, setLinkProblem] = useState('');
    const [isChecking, setIsChecking] = useState(true);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { showNotification } = useNotification();
    const authApi = useAuthApi();

    const [validation, setValidation] = useState<PasswordValidation>(validatePassword(''));

    // asks the backend whether the link can still be used and whose it is
    const checkLink = (tokenParam: string) => {
        setIsChecking(true);
        authApi.resetTokenStatus({ token: tokenParam })
            .then((status) => {
                if (status.status === 'VALID') {
                    setUsername(status.loginId ?? '');
                    setLinkProblem('');
                }
                else {
                    setLinkProblem(LINK_MESSAGES[status.status] ?? LINK_MESSAGES.UNKNOWN);
                }
            })
            .catch(() => setLinkProblem(CHECK_FAILED))
            .finally(() => setIsChecking(false));
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get('token');

        if (!tokenParam) {
            setLinkProblem(LINK_MESSAGES.UNKNOWN);
            setIsChecking(false);
            return;
        }

        setToken(tokenParam);
        checkLink(tokenParam);
    }, [location]);

    const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const password = e.target.value;
        setNewPassword(password);
        setValidation(validatePassword(password));
    };

    const isPasswordValid = meetsRules(validation);
    const doPasswordsMatch = newPassword === confirmPassword;

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isPasswordValid) {
            showNotification(NotificationType.ERROR, 'Please ensure your password meets all requirements');
            return;
        }

        if (!doPasswordsMatch) {
            showNotification(NotificationType.ERROR, 'Passwords do not match');
            return;
        }

        try {
            setIsResetting(true);
            await authApi.performResetPassword({ token: token, password: newPassword });
            showNotification(NotificationType.SUCCESS, 'Password reset successfully. Sign in with your new password.');

            // Redirect to login page after successful reset
            setTimeout(() => navigate('/login'), 2000);
        } catch (error) {
            // the message is already shown; the link may have been used or expired meanwhile
            console.error(error);
            checkLink(token);
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-start bg-cover bg-center bg-no-repeat"
             style={{ backgroundImage: `url(${houseImage})` }}
        >
            {/* Content container */}
            <div className="relative z-10 w-full max-w-md px-4 sm:px-6 min-h-screen py-4 sm:py-6 flex flex-col justify-between">
                {/* Logo section */}
                <div className="text-center pt-4 sm:pt-6">
                    <div className="flex justify-center mb-4 sm:mb-8">
                        <img
                            src={stewartLogo}
                            alt="Stewart Logo"
                            className="h-14 sm:h-16 w-auto object-contain"
                        />
                    </div>
                </div>

                {/* Reset Card */}
                <div className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-8 mx-auto w-full">
                    <div className="mb-4 sm:mb-6 text-center">
                        <h1 className="text-xl sm:text-2xl font-bold text-blue-800">Create New Password</h1>
                        {!linkProblem && !isChecking && (
                            <p className="text-sm text-gray-600 mt-1">Enter a new password for your account</p>
                        )}
                    </div>

                    {isChecking && (
                        <div className="flex justify-center py-6">
                            <div className="h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {!isChecking && linkProblem && (
                        <div className="space-y-4">
                            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
                                {linkProblem}
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/reset-password')}
                                className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium
                                         rounded-md hover:bg-blue-700 focus:outline-none
                                         focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                         flex items-center justify-center transition-colors"
                            >
                                Send me a new link
                            </button>
                        </div>
                    )}

                    {!isChecking && !linkProblem && (
                        <form onSubmit={handleResetPassword} className="space-y-4 sm:space-y-6">
                            {/* Username, from the link */}
                            <div>
                                <label htmlFor="username" className="block mb-1.5 text-sm font-medium text-gray-700">
                                    Username
                                </label>
                                <input
                                    id="username"
                                    type="text"
                                    value={username}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100
                                             text-gray-700 text-sm"
                                />
                            </div>

                            <div>
                                <label htmlFor="new-password" className="block mb-1.5 text-sm font-medium text-gray-700">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="new-password"
                                        type={showPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={handleNewPasswordChange}
                                        required
                                        autoComplete="new-password"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md
                                                 focus:outline-none focus:ring-2 focus:ring-blue-500
                                                 focus:border-blue-500 text-sm"
                                        placeholder="Enter your new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4 text-gray-500" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-gray-500" />
                                        )}
                                    </button>
                                </div>

                                <PasswordRequirements validation={validation} />
                            </div>

                            <div>
                                <label htmlFor="confirm-password" className="block mb-1.5 text-sm font-medium text-gray-700">
                                    Confirm New Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="confirm-password"
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        autoComplete="new-password"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md
                                                 focus:outline-none focus:ring-2 focus:ring-blue-500
                                                 focus:border-blue-500 text-sm"
                                        placeholder="Confirm your new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff className="h-4 w-4 text-gray-500" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-gray-500" />
                                        )}
                                    </button>
                                </div>
                                {confirmPassword && !doPasswordsMatch && (
                                    <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={!isPasswordValid || !doPasswordsMatch || isResetting}
                                className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium
                                         rounded-md hover:bg-blue-700 focus:outline-none
                                         focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                         disabled:opacity-50 disabled:cursor-not-allowed
                                         flex items-center justify-center transition-colors"
                            >
                                {isResetting ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Resetting...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                        </form>
                    )}

                    {/* Navigation links */}
                    <div className="mt-6">
                        <div className="flex flex-col space-y-2">
                            <button
                                onClick={() => window.history.back()}
                                className="bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 transition duration-200"
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="py-4 text-center text-white/90 text-sm">
                    © {new Date().getFullYear()} Stewart Valuation Intelligence
                </div>
            </div>
        </div>
    );
};
